import { useRef, useState } from "react";
import { CloudArrowUp, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl } from "../lib/api";

export default function MediaUpload({ onUploaded, label = "Upload flyer or video", imageOnly = false, videoOnly = false, clearAfterUpload = false }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imageOnly && !file.type.startsWith("image/")) {
      toast.error("Choose an image file");
      e.target.value = "";
      return;
    }
    if (videoOnly && !file.type.startsWith("video/")) {
      toast.error("Choose a video file (mp4 or webm)");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await API.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview({ ...data, abs: mediaUrl(data.url) });
      onUploaded(data);
      if (clearAfterUpload) setPreview(null);
      toast.success("Media uploaded");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="media-upload">
      <input ref={inputRef} type="file" accept={imageOnly ? "image/jpeg,image/png,image/webp,image/gif" : videoOnly ? "video/mp4,video/webm" : "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"}
        className="hidden" onChange={handle} data-testid="media-file-input" />
      {!preview ? (
        <button type="button" data-testid="media-upload-btn" onClick={() => inputRef.current?.click()} disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-black/40 px-4 py-4 text-xs font-bold text-zinc-400 transition-colors duration-200 hover:border-neon-gold/50 hover:text-neon-gold disabled:opacity-50">
          <CloudArrowUp size={18} /> {busy ? "Uploading..." : label}
        </button>
      ) : (
        <div className="relative overflow-hidden rounded-xl border border-white/10">
          {preview.content_type.startsWith("image/") ? (
            <img src={preview.abs} alt="Event media" className="max-h-40 w-full object-cover" />
          ) : (
            <video src={preview.abs} controls className="max-h-40 w-full" />
          )}
          <button type="button" data-testid="media-remove-btn"
            onClick={() => { setPreview(null); onUploaded(null); }}
            className="glass absolute right-2 top-2 rounded-full p-1.5">
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
